import type { ReactNode } from 'react';

/**
 * `.banner` — a soft-tinted block that explains a situation rather than reporting a failed
 * action: «Без машины нельзя оформить заявку», «Это необратимо», «Нельзя прямо сейчас».
 *
 * Metrics from the prototype: gap 10, padding 12×14, radius 16, 13px/1.4, title on its own line.
 *
 * Only the danger tone recolours its text. The other three keep `ink` on their tinted ground,
 * which is what keeps a success banner readable in dark mode — «unifying» all four to use the
 * accent colour is the mistake this comment exists to prevent.
 */
type Tone = 'info' | 'warning' | 'success' | 'danger';

const TONES: Record<Tone, string> = {
  info: 'bg-info-soft text-ink',
  warning: 'bg-warning-soft text-ink',
  success: 'bg-success-soft text-ink',
  danger: 'bg-accent-soft text-accent-soft-ink',
};

const ICON_TONES: Record<Tone, string> = {
  info: 'text-info',
  warning: 'text-warning',
  success: 'text-success',
  danger: 'text-accent-soft-ink',
};

export function Banner({
  tone = 'info',
  title,
  icon,
  children,
  action,
}: {
  tone?: Tone;
  title?: string;
  icon?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`flex gap-2.5 rounded-md px-3.5 py-3 text-[13px] leading-[1.4] ${TONES[tone]}`}>
      {icon && <span className={`mt-px shrink-0 ${ICON_TONES[tone]}`}>{icon}</span>}
      <div className="min-w-0 flex-1">
        {title && <strong className="mb-0.5 block font-bold">{title}</strong>}
        {children}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}
